import { Link } from 'react-router-dom'
import type {
  AttackTechnique,
  VulnCwe,
  ThreatActor,
} from '@/lib/types'

export interface AttackAndWeaknessesCardProps {
  techniques: AttackTechnique[]
  cwes: VulnCwe[]
  /**
   * Map from technique `externalId` (e.g. "T1190") to threat actors that use
   * it. Driver page computes this once via a single query joining
   * `actor_techniques` with `threat_actors`. For V6 go-live we pass `{}` —
   * actor hyperlinks light up in a follow-up PR when the join query lands.
   */
  actorsByTechnique?: Record<string, ThreatActor[]>
}

/**
 * V6 — Card 3: MITRE ATT&CK & Weaknesses.
 *
 * Combines two enrichment signals:
 * - ATT&CK techniques enabled by the vuln (from `vuln_techniques` ⋈
 *   `attack_techniques`).
 * - CWE weakness classes associated with the CVE (from `vuln_cwes`).
 *
 * Hidden only when BOTH lists are empty — a CVE with CWEs but no techniques
 * (or vice versa) still renders the populated half.
 *
 * When `actorsByTechnique[externalId]` returns one or more threat actors,
 * each actor's name renders as a hyperlink into `/threat-actors/:id` so
 * CISOs can pivot from "this CVE enables T1190" → "APT29 uses T1190" in a
 * single click.
 */
export function AttackAndWeaknessesCard({
  techniques,
  cwes,
  actorsByTechnique = {},
}: AttackAndWeaknessesCardProps) {
  if (techniques.length === 0 && cwes.length === 0) return null

  return (
    <section
      data-testid="attack-weaknesses-card"
      className="rounded-lg border border-border bg-card p-4 my-4"
      aria-label="MITRE ATT&CK and Weaknesses"
    >
      <h3 className="text-sm font-semibold">
        MITRE ATT&amp;CK &amp; Weaknesses
      </h3>

      {techniques.length > 0 && (
        <div className="mt-2">
          <h4 className="text-xs uppercase text-muted-foreground">
            Techniques enabled
          </h4>
          <ul className="mt-1 space-y-1 text-sm">
            {techniques.map((tech) => {
              const actors = actorsByTechnique[tech.externalId] ?? []
              return (
                <li key={tech.externalId}>
                  <span className="font-mono">{tech.externalId}</span>
                  {' — '}
                  <span>{tech.name}</span>
                  {actors.length > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (
                      {actors.map((a, i) => {
                        // Some schemas expose a `slug`; fall back to `id` when
                        // it is absent so existing ThreatActor rows still link.
                        const linkTarget =
                          (a as unknown as { slug?: string }).slug ?? a.id
                        return (
                          <span key={a.id}>
                            {i > 0 && ', '}
                            <Link
                              to={`/threat-actors/${linkTarget}`}
                              className="text-primary hover:underline"
                            >
                              {a.name}
                            </Link>
                          </span>
                        )
                      })}
                      )
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {cwes.length > 0 && (
        <div className="mt-3">
          <h4 className="text-xs uppercase text-muted-foreground">
            Weakness class
          </h4>
          <ul className="mt-1 space-y-1 text-sm">
            {cwes.map((cw) => (
              <li key={cw.cweId}>
                <span className="font-mono">{cw.cweId}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
